import { createContext, useContext, useState, ReactNode } from 'react';
import { useBoolean } from '@/hooks';

interface IIdsManagerContext {
  ids: string[];
  addIds: (ids: string[]) => void;
  removeIds: (ids: string[]) => void;
  clear: () => void;
  setIds: (ids: string[]) => void;
  selectedAll: boolean;
  toggleSelectAll: () => void;
  turnOffSelectedAll: () => void;
}

const IdsManagerContext = createContext<IIdsManagerContext | undefined>(undefined);

export const IdsManagerProvider = ({ children }: { children: ReactNode }) => {
  const [ids, setIdsState] = useState<string[]>([]);
  const { value: selectedAll, toggle: toggleSelectAll, setFalse: turnOffSelectedAll } = useBoolean();

  // State callbacks
  const addIds = (idsToAdd: string[]) => {
    setIdsState((prev) => {
      const uniqueIds = Array.from(new Set([...prev, ...idsToAdd]));
      return uniqueIds;
    });
  };

  const setIds = (idsArr: string[]) => {
    setIdsState(idsArr);
  };

  const removeIds = (idsToRemove: string[]) => {
    setIdsState((prev) => prev.filter((existingId) => !idsToRemove.includes(existingId)));
    turnOffSelectedAll();
  };

  const clear = () => {
    setIdsState([]);
    turnOffSelectedAll();
  };

  return (
    <IdsManagerContext.Provider
      value={{
        ids,
        addIds,
        removeIds,
        clear,
        setIds,
        selectedAll,
        toggleSelectAll,
        turnOffSelectedAll,
      }}
    >
      {children}
    </IdsManagerContext.Provider>
  );
};

export const useIdsManager = () => {
  const context = useContext(IdsManagerContext);
  if (!context) {
    throw new Error('useIdsManager must be used within an IdsManagerProvider');
  }
  return context;
};
